import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageProps, User } from "../types/auth";

import styles from './styles/MemberEnrollPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Member extends User {
  role: string;
}

interface Curriculum {
  curriculum_id: number;
  name: string;
}

interface RouteParams {
  [key: string]: string | undefined;
  member_id?: string;
}

const MemberEnrollPage: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const [member, setMember] = useState<Member | null>(null);
  const [availableCurriculums, setAvailableCurriculums] = useState<Curriculum[]>([]);
  const [enrolledCurriculums, setEnrolledCurriculums] = useState<Curriculum[]>([]);
  const [memberError, setMemberError] = useState<string>("");
  const [availableCurriculumsError, setAvailableCurriculumsError] = useState<string>("");
  const [enrolledCurriculumsError, setEnrolledCurriculumsError] = useState<string>("");
  const [actionError, setActionError] = useState<string>("");
  const navigate = useNavigate();
  const { member_id } = useParams<RouteParams>();

  useEffect(() => {
    const fetchMemberDetails = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/users/${member_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setMemberError(errorData.error || "Failed to fetch member details.");
          return;
        }

        const memberData: Member = await response.json();
        setMember(memberData);
      } catch (err) {
        setMemberError("An unexpected error occurred while fetching member details.");
      }
    };

    const fetchCurriculums = async () => {
      try {
        // Fetch enrolled curriculums
        const enrolledResponse = await fetch(`${API_BASE_URL}/api/users/${member_id}/curriculums`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        let enrolledData: Curriculum[] = [];
        if (!enrolledResponse.ok) {
          const errorData = await enrolledResponse.json();
          setEnrolledCurriculumsError(errorData.error || "Failed to fetch enrolled curriculums.");
        } else {
          const responseData = await enrolledResponse.json();
          // Handle null or undefined response
          enrolledData = Array.isArray(responseData) ? responseData : [];
          setEnrolledCurriculums(enrolledData);
        }

        // Fetch all curriculums
        const allResponse = await fetch(`${API_BASE_URL}/api/curriculums`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!allResponse.ok) {
          const errorData = await allResponse.json();
          setAvailableCurriculumsError(errorData.error || "Failed to fetch available curriculums.");
          return;
        }

        const responseData = await allResponse.json();
        // Handle null or undefined response
        const allData: Curriculum[] = Array.isArray(responseData) ? responseData : [];
        
        if (allData.length === 0) {
          // No curriculums exist in the system
          setAvailableCurriculums([]);
          return;
        }

        // Only filter if we have enrolled data and the enrolled response was successful
        if (enrolledResponse.ok) {
          const available = allData.filter(
            (curriculum) => !enrolledData.some((enrolled) => enrolled.curriculum_id === curriculum.curriculum_id)
          );
          setAvailableCurriculums(available);
        } else {
          setAvailableCurriculums(allData);
        }
      } catch (err) {
        setAvailableCurriculumsError("An unexpected error occurred while fetching curriculums.");
        setEnrolledCurriculumsError("An unexpected error occurred while fetching curriculums.");
      }
    };

    if (member_id) {
      fetchMemberDetails();
      fetchCurriculums();
    }
  }, [member_id, token, selectedOrgID]);

  const handleEnroll = async (curriculumID: number) => {
    setActionError("");
    try {
      const response = await fetch(`${API_BASE_URL}/api/curriculums/${curriculumID}/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
        body: JSON.stringify({
          user_id: parseInt(member_id || "0", 10),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setActionError(errorData.error || "Failed to enroll in curriculum.");
        return;
      }

      navigate(`/members/${member_id}`);
    } catch (err) {
      setActionError("An unexpected error occurred while enrolling in curriculum.");
    }
  };

  const handleUnenroll = async (curriculumID: number) => {
    setActionError("");
    try {
      const response = await fetch(`${API_BASE_URL}/api/curriculums/${curriculumID}/users/${member_id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        setActionError(errorData.error || "Failed to unenroll from curriculum.");
        return;
      }

      navigate(`/members/${member_id}`);
    } catch (err) {
      setActionError("An unexpected error occurred while unenrolling from curriculum.");
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Manage Curriculum Enrollment</h1>

      {memberError && <p className={styles.errorMessage}>Member Error: {memberError}</p>}

      {member ? (
        <p className={styles.memberInfo}>
          <strong>Member</strong>:
          <a href={`/members/${member_id}`} className={styles.memberLink}>
            {member.display_name || member.username} ({member.username})
          </a>
        </p>
      ) : !memberError && (
        <p className={styles.memberInfo}>Loading member information...</p>
      )}

      {actionError && <p className={styles.errorMessage}>Action Error: {actionError}</p>}

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Available Curriculums</h3>
        {availableCurriculumsError && (
          <p className={styles.errorMessage}>{availableCurriculumsError}</p>
        )}
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.tableHeader}>Curriculum</th>
              <th className={styles.tableHeader}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {availableCurriculums.length > 0 ? (
              availableCurriculums.map((curriculum) => (
                <tr key={curriculum.curriculum_id} className={styles.tableRow}>
                  <td className={styles.tableCell}>{curriculum.name}</td>
                  <td className={styles.tableCell}>
                    <button 
                      className={styles.enrollButton}
                      onClick={() => handleEnroll(curriculum.curriculum_id)}
                    >
                      Enroll
                    </button>
                  </td>
                </tr>
              ))
            ) : !availableCurriculumsError ? (
              <tr>
                <td colSpan={2} className={styles.tableCell}>No available curriculums.</td>
              </tr>
            ) : (
              <tr>
                <td colSpan={2} className={styles.tableCell}>Unable to load available curriculums.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Enrolled Curriculums</h3>
        {enrolledCurriculumsError && (
          <p className={styles.errorMessage}>{enrolledCurriculumsError}</p>
        )}
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.tableHeader}>Curriculum</th>
              <th className={styles.tableHeader}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {enrolledCurriculums.length > 0 ? (
              enrolledCurriculums.map((curriculum) => (
                <tr key={curriculum.curriculum_id} className={styles.tableRow}>
                  <td className={styles.tableCell}>{curriculum.name}</td>
                  <td className={styles.tableCell}>
                    <button 
                      className={styles.unenrollButton}
                      onClick={() => handleUnenroll(curriculum.curriculum_id)}
                    >
                      Unenroll
                    </button>
                  </td>
                </tr>
              ))
            ) : !enrolledCurriculumsError ? (
              <tr>
                <td colSpan={2} className={styles.tableCell}>No enrolled curriculums.</td>
              </tr>
            ) : (
              <tr>
                <td colSpan={2} className={styles.tableCell}>Unable to load enrolled curriculums.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MemberEnrollPage;
